import React, { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, serverTimestamp, doc, getDoc, setDoc, query, where, limit, getDocs, deleteDoc } from 'firebase/firestore';

const QRGenerator = ({ onClose }) => {
  const [, setUser] = useState(null);
  const [qrData, setQrData] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    title: '',
    phone: '',
    email: '',
    website: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previousQRs, setPreviousQRs] = useState([]);
  const [showPreviousQRs, setShowPreviousQRs] = useState(false);
  const saveTimeoutRef = useRef(null);

  const loadPreviousQRs = useCallback(async (userId) => {
    try {
      // 暫時使用簡化查詢，避免需要索引
      const qrQuery = query(
        collection(db, 'generatedQRCodes'),
        where('userId', '==', userId),
        limit(10) // 載入最近10個記錄，然後在客戶端排序
      );
      
      const querySnapshot = await getDocs(qrQuery);
      const qrRecords = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.formData && data.timestamp) {
          qrRecords.push({
            id: doc.id,
            formData: data.formData,
            timestamp: data.timestamp,
            qrDataUrl: data.qrDataUrl
          });
        }
      });
      
      // 在客戶端按時間排序並限制數量
      const sortedRecords = qrRecords
        .sort((a, b) => {
          const aTime = a.timestamp.toDate ? a.timestamp.toDate() : new Date(a.timestamp.seconds * 1000);
          const bTime = b.timestamp.toDate ? b.timestamp.toDate() : new Date(b.timestamp.seconds * 1000);
          return bTime - aTime; // 降序排列（最新的在前）
        })
        .slice(0, 5); // 只取前5個
      
      return sortedRecords;
    } catch (error) {
      console.error('載入歷史QR記錄失敗:', error);
      return [];
    }
  }, []);

  const loadUserFormData = useCallback(async (currentUser) => {
    try {
      // 同時載入用戶檔案和歷史QR記錄
      const [userProfileDoc, previousQRsData] = await Promise.all([
        getDoc(doc(db, 'userProfiles', currentUser.uid)),
        loadPreviousQRs(currentUser.uid)
      ]);

      setPreviousQRs(previousQRsData);
      
      // 優先使用最新的QR記錄，如果有的話
      if (previousQRsData.length > 0) {
        setFormData(previousQRsData[0].formData);
        setQrData(previousQRsData[0].qrDataUrl); // 也載入QR碼圖片
      } else if (userProfileDoc.exists()) {
        const userData = userProfileDoc.data();
        if (userData.businessCardData) {
          setFormData(userData.businessCardData);
        } else {
          // 使用預設值
          setFormData(prev => ({
            ...prev,
            name: currentUser.displayName || '',
            email: currentUser.email || ''
          }));
        }
      } else {
        // 使用預設值
        setFormData(prev => ({
          ...prev,
          name: currentUser.displayName || '',
          email: currentUser.email || ''
        }));
      }
    } catch (error) {
      console.error('載入用戶名片資料失敗:', error);
      // 如果載入失敗，使用預設值
      setFormData(prev => ({
        ...prev,
        name: currentUser.displayName || '',
        email: currentUser.email || ''
      }));
    }
  }, [loadPreviousQRs]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await loadUserFormData(currentUser);
      }
    });
    return () => {
      unsubscribe();
      // 清除待執行的儲存操作
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [loadUserFormData]);

  const generateVCard = () => {
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${formData.name}
N:${formData.name};;;;
ORG:${formData.company}
TITLE:${formData.title}
TEL:${formData.phone}
EMAIL:${formData.email}
URL:${formData.website}
ADR:;;${formData.address};;;;
END:VCARD`;
    return vcard;
  };

  const saveQRToFirebase = async (vcard, qrDataUrl) => {
    try {
      await addDoc(collection(db, 'generatedQRCodes'), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        userName: auth.currentUser.displayName || auth.currentUser.email,
        formData: formData,
        vcard: vcard,
        qrDataUrl: qrDataUrl,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('儲存 QR Code 到 Firebase 失敗:', error);
      throw error;
    }
  };

  const generateQR = async () => {
    setLoading(true);
    try {
      const vcard = generateVCard();
      const qrDataUrl = await QRCode.toDataURL(vcard, {
        width: 300,
        margin: 2,
        color: {
          dark: '#6c638f',
          light: '#ffffff'
        }
      });
      setQrData(qrDataUrl);
      
      // 儲存到 Firebase
      if (auth.currentUser) {
        await saveQRToFirebase(vcard, qrDataUrl);
      }
    } catch (error) {
      console.error('QR Code 生成失敗:', error);
      alert('QR Code 生成失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  const saveFormDataToFirebase = async (newFormData) => {
    try {
      setSaving(true);
      if (auth.currentUser) {
        const userProfileDoc = doc(db, 'userProfiles', auth.currentUser.uid);
        await setDoc(userProfileDoc, {
          businessCardData: newFormData,
          lastUpdated: serverTimestamp()
        }, { merge: true });
      }
    } catch (error) {
      console.error('儲存用戶名片資料失敗:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    setFormData(newFormData);
    
    // 防抖保存到 Firebase (延遲1秒後保存)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveFormDataToFirebase(newFormData);
    }, 1000);
  };

  const downloadQR = () => {
    if (!qrData) return;
    
    const link = document.createElement('a');
    link.href = qrData;
    link.download = `business-card-qr-${formData.name || 'unnamed'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareQR = async () => {
    if (!qrData) return;
    
    try {
      // 將 data URL 轉換為 Blob
      const response = await fetch(qrData);
      const blob = await response.blob();
      
      if (navigator.share) {
        const file = new File([blob], `business-card-qr-${formData.name || 'unnamed'}.png`, {
          type: 'image/png'
        });
        
        await navigator.share({
          title: `${formData.name}的電子名片 QR Code`,
          text: '掃描此 QR Code 以添加聯絡人',
          files: [file]
        });
      } else {
        // 降級到複製連結
        navigator.clipboard.writeText(generateVCard());
        alert('名片資訊已複製到剪貼簿');
      }
    } catch (error) {
      console.error('分享失敗:', error);
      alert('分享失敗，請重試');
    }
  };

  const clearFormData = async () => {
    const defaultFormData = {
      name: auth.currentUser?.displayName || '',
      company: '',
      title: '',
      phone: '',
      email: auth.currentUser?.email || '',
      website: '',
      address: ''
    };
    setFormData(defaultFormData);
    setQrData('');
    
    // 清除 Firebase 中的名片資料
    try {
      if (auth.currentUser) {
        const userProfileDoc = doc(db, 'userProfiles', auth.currentUser.uid);
        await setDoc(userProfileDoc, {
          businessCardData: defaultFormData,
          lastUpdated: serverTimestamp()
        }, { merge: true });
      }
    } catch (error) {
      console.error('清除用戶名片資料失敗:', error);
    }
  };

  const loadPreviousQRData = (qrRecord) => {
    setFormData(qrRecord.formData);
    setQrData(qrRecord.qrDataUrl);
    setShowPreviousQRs(false);
    
    // 同時更新用戶檔案
    if (auth.currentUser) {
      saveFormDataToFirebase(qrRecord.formData);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deleteQRRecord = async (recordId) => {
    try {
      await deleteDoc(doc(db, 'generatedQRCodes', recordId));
      
      // 重新載入歷史記錄
      if (auth.currentUser) {
        const updatedQRs = await loadPreviousQRs(auth.currentUser.uid);
        setPreviousQRs(updatedQRs);
        
        // 如果刪除的是當前顯示的記錄，清空當前狀態
        const currentRecordExists = updatedQRs.some(record => record.qrDataUrl === qrData);
        if (!currentRecordExists) {
          setQrData('');
          // 如果還有其他記錄，載入最新的
          if (updatedQRs.length > 0) {
            setFormData(updatedQRs[0].formData);
            setQrData(updatedQRs[0].qrDataUrl);
          }
        }
      }
      
      alert('QR記錄已刪除');
    } catch (error) {
      console.error('刪除QR記錄失敗:', error);
      alert('刪除失敗，請重試');
    }
  };


  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>電子名片 QR Code</h2>
          <div className="header-actions">
            {saving && <span className="saving-indicator">儲存中...</span>}
            <button onClick={onClose} className="close-btn">✕</button>
          </div>
        </div>
        
        <div className="modal-body">
          <div className="form-section">
            <h3>名片資訊</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>姓名 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="請輸入姓名"
                  required
                />
              </div>
              <div className="form-field">
                <label>公司</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="請輸入公司名稱"
                />
              </div>
              <div className="form-field">
                <label>職位</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="請輸入職位"
                />
              </div>
              <div className="form-field">
                <label>電話</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="請輸入電話"
                />
              </div>
              <div className="form-field">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="請輸入Email"
                />
              </div>
              <div className="form-field">
                <label>網站</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="請輸入網站"
                />
              </div>
              <div className="form-field full-width">
                <label>地址</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="請輸入地址"
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button 
                onClick={generateQR}
                disabled={loading || !formData.name}
                className="generate-btn"
              >
                {loading ? '生成中...' : '生成電子名片 QR'}
              </button>
              <button 
                onClick={clearFormData}
                className="clear-btn"
                type="button"
              >
                清除資料
              </button>
              {previousQRs.length > 0 && (
                <button 
                  onClick={() => setShowPreviousQRs(!showPreviousQRs)}
                  className="history-btn"
                  type="button"
                >
                  {showPreviousQRs ? '隱藏歷史' : `載入歷史(${previousQRs.length})`}
                </button>
              )}
            </div>
            
            {/* 歷史QR記錄 */}
            {showPreviousQRs && previousQRs.length > 0 && (
              <div className="previous-qrs-section">
                <h4>之前生成的QR記錄</h4>
                <div className="previous-qrs-list">
                  {previousQRs.map((qrRecord) => (
                    <div key={qrRecord.id} className="previous-qr-item">
                      <div className="qr-preview">
                        <img 
                          src={qrRecord.qrDataUrl} 
                          alt="Previous QR" 
                          className="qr-thumbnail"
                        />
                      </div>
                      <div className="qr-info">
                        <div className="qr-title">
                          {qrRecord.formData.name || '未命名'}
                          {qrRecord.formData.company && ` - ${qrRecord.formData.company}`}
                        </div>
                        <div className="qr-timestamp">
                          {formatTimestamp(qrRecord.timestamp)}
                        </div>
                        <div className="qr-details">
                          {qrRecord.formData.title && <span>職位: {qrRecord.formData.title}</span>}
                          {qrRecord.formData.phone && <span>電話: {qrRecord.formData.phone}</span>}
                        </div>
                      </div>
                      <div className="qr-item-actions">
                        <button 
                          onClick={() => loadPreviousQRData(qrRecord)}
                          className="load-qr-btn"
                        >
                          載入
                        </button>
                        <button 
                          onClick={() => deleteQRRecord(qrRecord.id)}
                          className="delete-qr-btn"
                        >
                          刪除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {qrData && (
            <div className="qr-section">
              <h3>您的電子名片 QR Code</h3>
              <div className="qr-display">
                <img src={qrData} alt="Electronic Business Card QR Code" />
              </div>
              <div className="qr-actions">
                <button onClick={downloadQR} className="download-btn">
                  下載 QR Code
                </button>
                <button onClick={shareQR} className="share-btn">
                  分享
                </button>
              </div>
              <div className="qr-saved">
                <p>✓ 此電子名片 QR Code 已儲存到您的帳戶中</p>
              </div>
              <p className="qr-info">
                他人可以掃描此 QR Code 直接將您的聯絡資訊添加到通訊錄中
              </p>
            </div>
          )}
        </div>
      </div>

      <style jsx="true">{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
          padding: 20px;
        }

        .modal-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 28px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.1);
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .saving-indicator {
          font-size: 12px;
          color: rgba(102, 126, 234, 0.8);
          font-weight: 500;
          opacity: 0.8;
        }

        .modal-header h2 {
          margin: 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 24px;
          font-weight: 600;
          background: linear-gradient(135deg, #3f51b5, #673ab7);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: rgba(108, 99, 143, 0.6);
          padding: 8px;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          color: rgba(108, 99, 143, 0.9);
        }

        .modal-body {
          padding: 28px;
          overflow-y: auto;
          flex: 1;
        }

        .form-section {
          margin-bottom: 30px;
        }

        .form-section h3 {
          margin: 0 0 20px 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 18px;
          font-weight: 600;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .form-field {
          display: flex;
          flex-direction: column;
        }

        .form-field.full-width {
          grid-column: 1 / -1;
        }

        .form-field label {
          margin-bottom: 6px;
          color: rgba(108, 99, 143, 0.8);
          font-weight: 600;
          font-size: 14px;
        }

        .form-field input {
          padding: 12px 16px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          font-size: 14px;
          color: rgba(108, 99, 143, 0.9);
          transition: all 0.3s ease;
          outline: none;
        }

        .form-field input:focus {
          border-color: rgba(102, 126, 234, 0.6);
          background: rgba(255, 255, 255, 0.5);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .generate-btn {
          flex: 1;
          padding: 14px 28px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .generate-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
        }

        .generate-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .clear-btn {
          padding: 14px 24px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          color: rgba(108, 99, 143, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .clear-btn:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
          border-color: rgba(255, 255, 255, 0.6);
        }

        .history-btn {
          padding: 14px 20px;
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
        }

        .history-btn:hover {
          background: linear-gradient(135deg, #f57c00 0%, #e65100 100%);
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(255, 152, 0, 0.4);
        }

        .previous-qrs-section {
          margin-top: 24px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
        }

        .previous-qrs-section h4 {
          color: rgba(108, 99, 143, 0.9);
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
        }

        .previous-qrs-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 300px;
          overflow-y: auto;
        }

        .previous-qr-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          transition: all 0.2s ease;
        }

        .previous-qr-item:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        .qr-thumbnail {
          width: 50px;
          height: 50px;
          border-radius: 8px;
          object-fit: cover;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .qr-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .qr-title {
          font-weight: 600;
          color: rgba(108, 99, 143, 0.9);
          font-size: 14px;
        }

        .qr-timestamp {
          font-size: 12px;
          color: rgba(108, 99, 143, 0.6);
        }

        .qr-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .qr-details span {
          font-size: 11px;
          color: rgba(108, 99, 143, 0.7);
        }

        .load-qr-btn {
          padding: 8px 16px;
          background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .load-qr-btn:hover {
          background: linear-gradient(135deg, #388e3c 0%, #689f38 100%);
          transform: translateY(-1px);
        }

        .qr-item-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .delete-qr-btn {
          padding: 8px 16px;
          background: linear-gradient(135deg, #f44336 0%, #e57373 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .delete-qr-btn:hover {
          background: linear-gradient(135deg, #d32f2f 0%, #f44336 100%);
          transform: translateY(-1px);
        }

        .qr-section {
          border-top: 1px solid rgba(255, 255, 255, 0.3);
          padding-top: 30px;
          text-align: center;
        }

        .qr-section h3 {
          margin: 0 0 20px 0;
          color: rgba(108, 99, 143, 0.9);
          font-size: 18px;
          font-weight: 600;
        }

        .qr-display {
          background: white;
          padding: 20px;
          border-radius: 16px;
          display: inline-block;
          margin-bottom: 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .qr-display img {
          display: block;
          width: 300px;
          height: 300px;
        }

        .qr-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }

        .download-btn, .share-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .download-btn {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(86, 171, 47, 0.3);
        }

        .download-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(86, 171, 47, 0.4);
        }

        .share-btn {
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          color: rgba(108, 99, 143, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.4);
        }

        .share-btn:hover {
          background: rgba(255, 255, 255, 0.4);
          transform: translateY(-1px);
        }

        .qr-saved {
          background: rgba(76, 175, 80, 0.2);
          border: 1px solid rgba(76, 175, 80, 0.3);
          border-radius: 8px;
          padding: 8px 12px;
          margin: 12px 0;
          text-align: center;
        }

        .qr-saved p {
          color: rgba(46, 125, 50, 0.9);
          font-size: 12px;
          margin: 0;
          font-weight: 500;
        }

        .qr-info {
          color: rgba(108, 99, 143, 0.7);
          font-size: 14px;
          margin: 0;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .modal-content {
            max-height: 95vh;
            margin: 10px;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-actions {
            flex-direction: column;
            gap: 10px;
          }

          .generate-btn, .clear-btn, .history-btn {
            width: 100%;
          }

          .previous-qr-item {
            flex-direction: column;
            text-align: center;
          }

          .qr-info {
            text-align: center;
          }

          .qr-details {
            align-items: center;
          }

          .qr-item-actions {
            flex-direction: row;
            justify-content: center;
            width: 100%;
          }

          .load-qr-btn, .delete-qr-btn {
            flex: 1;
            max-width: 100px;
          }

          .qr-display img {
            width: 250px;
            height: 250px;
          }

          .qr-actions {
            flex-direction: column;
            width: 100%;
          }

          .download-btn, .share-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default QRGenerator;